export const KIOSK_STORAGE_KEY = "kiosk-display:active";

export const kioskStorageKey = (channelKey) => {
  const normalized = String(channelKey || "").trim().toLowerCase();
  return normalized ? `${KIOSK_STORAGE_KEY}:${normalized}` : KIOSK_STORAGE_KEY;
};

const I_CAN_RE = /^[-*•]?\s*i can\b/i;
const TEKS_START_RE = /^(\d+\.[a-z0-9]+(?:\/\d+\.[a-z0-9]+)*)(?:\s+(.+))?$/i;
const TEKS_MARKER_RE = /(\d+\.[a-z0-9]+(?:\/\d+\.[a-z0-9]+)*)/ig;

const stripBullet = (line = "") => line.replace(/^[-*•]\s*/, "").trim();

const toNormalizedLines = (text = "") =>
  String(text)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const splitLineByPipesAndRepeatedTeks = (line = "") => {
  const pipeParts = String(line || "")
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);

  return pipeParts.flatMap((part) => {
    const matches = Array.from(part.matchAll(TEKS_MARKER_RE));
    // Only split by repeated TEKS when the line starts with a TEKS marker.
    if (matches.length < 2 || Number(matches[0]?.index) !== 0) {
      return [part];
    }

    const segments = [];
    for (let i = 0; i < matches.length; i += 1) {
      const start = Number(matches[i]?.index || 0);
      const end = i + 1 < matches.length ? Number(matches[i + 1]?.index || part.length) : part.length;
      const chunk = part.slice(start, end).trim();
      if (chunk) segments.push(chunk);
    }
    return segments.length ? segments : [part];
  });
};

const toObjectiveLines = (text = "") =>
  toNormalizedLines(text).flatMap((line) => splitLineByPipesAndRepeatedTeks(line));

export const parseObjectiveText = (text = "") => {
  const lines = toObjectiveLines(text);

  const iCanStatements = lines
    .filter((line) => I_CAN_RE.test(line))
    .map((line) => stripBullet(line));

  const statementLines = lines.filter((line) => !I_CAN_RE.test(line));

  return {
    statement: statementLines.join(" "),
    iCanStatements,
  };
};

export const validateObjectiveTextForKiosk = (text = "") => {
  const lines = toObjectiveLines(text);
  const parsed = parseObjectiveText(text);
  const errors = [];
  const warnings = [];

  if (!lines.length) {
    errors.push("Add a learning objective before publishing to kiosk.");
  }

  if (!String(parsed.statement || "").trim()) {
    errors.push("Add at least one objective statement line (not only I can statements).");
  }

  const nonICanBullets = lines.filter(
    (line) => /^[-*•]/.test(line) && !I_CAN_RE.test(line),
  );
  if (nonICanBullets.length > 0) {
    warnings.push('Bullet lines not starting with "I can" will be treated as objective text.');
  }

  if (String(parsed.statement || "").length > 1200) {
    warnings.push("Objective statement is long; split objectives with | or TEKS markers for readability.");
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    parsed,
  };
};

export const normalizeICanStatement = (value = "") => {
  const raw = stripBullet(String(value || "").trim());
  if (!raw) return "";
  if (I_CAN_RE.test(raw)) {
    return raw.replace(/^i can\b/i, "I can").trim();
  }
  return `I can ${raw}`;
};

export const parseObjectiveTextToStructured = (text = "", fallbackTeksCode = "") => {
  const lines = toObjectiveLines(text);
  const objectives = [];
  let current = {
    teksCode: String(fallbackTeksCode || "").trim(),
    statement: "",
    iCanStatements: [],
  };

  const pushCurrent = () => {
    const next = {
      teksCode: String(current.teksCode || "").trim(),
      statement: String(current.statement || "").trim(),
      iCanStatements: Array.isArray(current.iCanStatements)
        ? current.iCanStatements.map((item) => normalizeICanStatement(item)).filter(Boolean)
        : [],
    };
    if (!next.teksCode && !next.statement && next.iCanStatements.length === 0) return;
    objectives.push(next);
  };

  lines.forEach((line) => {
    if (I_CAN_RE.test(line)) {
      current.iCanStatements.push(normalizeICanStatement(line));
      return;
    }

    const teksMatch = line.match(TEKS_START_RE);
    if (teksMatch) {
      const teksCode = String(teksMatch[1] || "").trim();
      const trailingStatement = String(teksMatch[2] || "").trim();

      if (current.statement || current.iCanStatements.length > 0) {
        pushCurrent();
        current = { teksCode, statement: "", iCanStatements: [] };
      } else {
        current.teksCode = teksCode || current.teksCode;
      }

      if (trailingStatement) {
        current.statement = current.statement
          ? `${current.statement} ${trailingStatement}`.trim()
          : trailingStatement;
      }
      return;
    }

    current.statement = current.statement
      ? `${current.statement} ${line}`.trim()
      : line;
  });

  pushCurrent();

  if (!objectives.length) {
    return [
      {
        teksCode: String(fallbackTeksCode || "").trim(),
        statement: "",
        iCanStatements: [],
      },
    ];
  }

  return objectives;
};

export const serializeStructuredObjectives = (objectives = []) =>
  (Array.isArray(objectives) ? objectives : [])
    .map((objective) => {
      const lines = [];
      const teksCode = String(objective?.teksCode || "").trim();
      const statement = String(objective?.statement || "").trim();
      const iCanStatements = Array.isArray(objective?.iCanStatements)
        ? objective.iCanStatements.map((item) => normalizeICanStatement(item)).filter(Boolean)
        : [];

      if (teksCode) lines.push(teksCode);
      if (statement) lines.push(statement);
      iCanStatements.forEach((line) => lines.push(`• ${line}`));
      return lines.join("\n").trim();
    })
    .filter(Boolean)
    .join("\n\n")
    .trim();

export const validateStructuredObjectives = (objectives = []) => {
  const rows = Array.isArray(objectives) ? objectives : [];
  const errors = [];
  const warnings = [];

  if (!rows.length) {
    errors.push("Add at least one objective.");
  }

  rows.forEach((objective, index) => {
    const label = `Objective ${index + 1}`;
    const statement = String(objective?.statement || "").trim();
    const iCanStatements = Array.isArray(objective?.iCanStatements)
      ? objective.iCanStatements.map((item) => normalizeICanStatement(item)).filter(Boolean)
      : [];

    if (!statement) {
      errors.push(`${label}: add a learning objective statement.`);
    }
    if (!iCanStatements.length) {
      warnings.push(`${label}: add at least one I can statement for student-facing clarity.`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const getKioskPayload = (channelKey = null) => {
  const raw = localStorage.getItem(kioskStorageKey(channelKey));
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
};

export const publishKioskPayload = (payload, channelKey = null) => {
  const next = {
    ...payload,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(kioskStorageKey(channelKey), JSON.stringify(next));
  return next;
};
